import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, Share2, Edit } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { AdminBar } from '../components/AdminBar';
import { useTitle } from '../hooks/useTitle';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  image_url: string;
  author: string;
  reading_time: number;
  published_at: string;
  category: string;
  tags: string[];
}

export function BlogPost() {
  const { id } = useParams();
  const { setTitle } = useTitle();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    fetchPost();
  }, [id]);

  useEffect(() => {
    if (post) {
      setTitle(`${post.title} - Blog Novster AI`);
    }
  }, [post, setTitle]);

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setPost(data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Chargement de l'article...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Article non trouvé</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />

      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={post.image_url}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-16">
            <Link 
              to="/blog" 
              className="inline-flex items-center text-white hover:text-orange-300 mb-8 group transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />
              Retour au blog
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slideUp max-w-4xl">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(post.author)}&background=orange&color=fff`}
                  alt={post.author}
                  className="w-10 h-10 rounded-full"
                />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(post.published_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{post.reading_time} min de lecture</span>
              </div>
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                <span>{post.category}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <article className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Social Share */}
            <div className="flex justify-end mb-8">
              <button
                onClick={() => navigator.share({
                  title: post.title,
                  text: post.title,
                  url: window.location.href
                })}
                className="inline-flex items-center gap-2 text-gray-500 hover:text-orange-500 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Partager
              </button>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t">
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Admin Edit Button */}
            {isAuthenticated && (
              <div className="mt-8 flex justify-end">
                <Link
                  to={`/admin/blog/edit/${post.id}`}
                  className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
                >
                  <Edit className="w-5 h-5" />
                  Modifier l'article
                </Link>
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}